import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { GameRulesSlide, HitsterEvent } from '../types';
import { normalizeGameRulesConfig, renderGameRulesTemplate } from '../utils/gameRules';
import './GameRules.css';

const GameRules: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const isEmbedded = searchParams.get('embedded') === '1';

  useEffect(() => {
    const loadEvent = async () => {
      try {
        const response = await fetch(`/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      }
      setLoading(false);
    };
    loadEvent();
  }, [eventId]);

  const rules = normalizeGameRulesConfig(event?.gameRules);
  const visibleSlides = (rules.slides || []).filter(slide => slide.type !== 'flyer' || !!event?.introFlyerUrl);
  const totalSlides = visibleSlides.length;

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const closeEmbeddedRules = useCallback(async () => {
    if (!isEmbedded) return;

    try {
      await fetch('/api/display-state/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'hitster',
          rulesEventId: null
        })
      });
    } catch (err) {
      console.error('Failed to close embedded game rules:', err);
    }
  }, [isEmbedded]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape' && isEmbedded) {
        e.preventDefault();
        void closeEmbeddedRules();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeEmbeddedRules, isEmbedded, nextSlide, prevSlide]);

  if (loading) {
    return <div className="game-rules loading">Laden...</div>;
  }

  if (!event) {
    return <div className="game-rules error">Evenement niet gevonden</div>;
  }

  const renderSlide = (slide: GameRulesSlide) => {
    const teams = event.teams || [];
    const icon = slide.icon ? <div className="slide-icon">{slide.icon}</div> : null;
    const hasFullscreenIllustration = !!slide.illustrationUrl;
    const illustration = slide.illustrationUrl ? (
      <img
        src={slide.illustrationUrl}
        alt={slide.title ? renderGameRulesTemplate(slide.title, event) : 'Slide illustratie'}
        className={hasFullscreenIllustration ? 'slide-illustration slide-illustration-fullscreen' : 'slide-illustration'}
      />
    ) : null;

    if (hasFullscreenIllustration) {
      return (
        <div key={slide.id} className="slide slide-fullscreen-illustration">
          {illustration}
        </div>
      );
    }

    switch (slide.type) {
      case 'title':
        return (
          <div key={slide.id} className="slide slide-title">
            {illustration}
            <h1>{slide.icon ? `${slide.icon} ` : ''}{renderGameRulesTemplate(slide.title || event.name, event)}</h1>
            {slide.subtitle && <p className="subtitle">{renderGameRulesTemplate(slide.subtitle, event)}</p>}
            {slide.hintText && <div className="slide-hint">{renderGameRulesTemplate(slide.hintText, event)}</div>}
          </div>
        );
      case 'flyer':
        return (
          <div key={slide.id} className="slide slide-flyer">
            {illustration}
            <img src={event.introFlyerUrl} alt="Introductieflyer" className="intro-flyer-img" />
          </div>
        );
      case 'text':
        return (
          <div key={slide.id} className="slide">
            {illustration}
            {icon}
            {slide.title && <h2>{renderGameRulesTemplate(slide.title, event)}</h2>}
            {(slide.lines || []).map((line, index) => (
              <p key={index} className="slide-text">{renderGameRulesTemplate(line, event)}</p>
            ))}
          </div>
        );
      case 'steps':
        return (
          <div key={slide.id} className="slide">
            {illustration}
            {icon}
            {slide.title && <h2>{renderGameRulesTemplate(slide.title, event)}</h2>}
            <div className="slide-steps">
              {(slide.lines || []).map((line, index) => (
                <div key={index} className="slide-step">
                  <span className="step-num">{index + 1}</span>
                  <p>{renderGameRulesTemplate(line, event)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 'scoring':
        return (
          <div key={slide.id} className="slide">
            {illustration}
            {icon}
            {slide.title && <h2>{renderGameRulesTemplate(slide.title, event)}</h2>}
            <div className="slide-scoring">
              {(slide.items || []).map((item, index, allItems) => (
                <div key={index} className={`score-row ${index === allItems.length - 1 ? 'total' : ''}`}>
                  <span className="score-points">{renderGameRulesTemplate(item.points, event)}</span>
                  <span className="score-desc">{renderGameRulesTemplate(item.label, event)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'position':
        return (
          <div key={slide.id} className="slide">
            {illustration}
            {icon}
            {slide.title && <h2>{renderGameRulesTemplate(slide.title, event)}</h2>}
            <div className="position-visual">
              <div className="pos-side left">
                <span className="pos-label">{renderGameRulesTemplate(slide.leftLabel, event)}</span>
                <span className="pos-desc">{renderGameRulesTemplate(slide.leftDescription, event)}</span>
              </div>
              <div className="pos-center">
                <span className="pos-year">{renderGameRulesTemplate(slide.centerLabel, event)}</span>
              </div>
              <div className="pos-side right">
                <span className="pos-label">{renderGameRulesTemplate(slide.rightLabel, event)}</span>
                <span className="pos-desc">{renderGameRulesTemplate(slide.rightDescription, event)}</span>
              </div>
            </div>
            {slide.footer && <p className="slide-text">{renderGameRulesTemplate(slide.footer, event)}</p>}
          </div>
        );
      case 'teams':
        return (
          <div key={slide.id} className="slide">
            {illustration}
            {icon}
            {slide.title && <h2>{renderGameRulesTemplate(slide.title, event)}</h2>}
            <div className="slide-teams">
              {teams.map((team, idx) => (
                <div key={team.id} className="slide-team">
                  <span className="team-num">{idx + 1}</span>
                  <span className="team-name">{team.name}</span>
                </div>
              ))}
              {teams.length === 0 && (
                <div className="slide-team-empty">{renderGameRulesTemplate(slide.emptyMessage, event)}</div>
              )}
            </div>
          </div>
        );
      case 'final':
        return (
          <div key={slide.id} className="slide slide-final">
            {illustration}
            {icon}
            {slide.title && <h2>{renderGameRulesTemplate(slide.title, event)}</h2>}
            {slide.summary && <p className="slide-text big">{renderGameRulesTemplate(slide.summary, event)}</p>}
            <div className="goodluck">
              <span>🎉</span>
              <p>{renderGameRulesTemplate(slide.message, event)}</p>
              <span>🎉</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const slides = visibleSlides.map(renderSlide).filter(Boolean);

  return (
    <div className="game-rules slideshow">
      {!isEmbedded && (
        <button className="back-btn no-print" onClick={() => navigate('/events')}>
          ← Terug
        </button>
      )}

      {isEmbedded && (
        <button className="back-btn close-btn no-print" onClick={() => void closeEmbeddedRules()}>
          ✕ Afsluiten
        </button>
      )}

      <div className="slides-container">
        {slides[currentSlide]}
      </div>

      <div className="slide-nav">
        <button className="nav-btn" onClick={prevSlide} disabled={currentSlide === 0}>
          ◀
        </button>
        <div className="slide-dots">
          {slides.map((_, idx) => (
            <span 
              key={idx} 
              className={`dot ${idx === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(idx)}
            />
          ))}
        </div>
        <button className="nav-btn" onClick={nextSlide} disabled={currentSlide === totalSlides - 1}>
          ▶
        </button>
      </div>

      <div className="slide-counter">
        {currentSlide + 1} / {totalSlides}
      </div>
    </div>
  );
};

export default GameRules;
